import { Button } from '@/components/ui/button';
import { Brand, Neutrals } from '@/constants/theme';
import { useAuth } from '@/hooks/use-auth';
import { getOnboardingCompleted } from '@/lib/onboarding-storage';
import { PENDING_INVITE_KEY } from '@/lib/invite-links';
import { supabase } from '@/lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

declare const window: {
  location?: { hash: string; search: string; origin: string };
} | undefined;

export default function VerifyEmailScreen() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [continuing, setContinuing] = useState(false);
  const router = useRouter();
  const hasCheckedVerification = useRef(false);

  useEffect(() => {
    if (hasCheckedVerification.current) return;
    hasCheckedVerification.current = true;

    const checkVerification = async () => {
      try {
        if (!supabase) {
          setError('Unable to verify. Please try again.');
          setLoading(false);
          return;
        }

        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
          const hash = window.location.hash;
          const search = window.location.search;

          if (hash) {
            const hashParams = new URLSearchParams(hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');
            const type = hashParams.get('type');

            if (accessToken && refreshToken && (type === 'signup' || type === 'email')) {
              const { data: { session }, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (sessionError) {
                setError('Failed to verify email. Please try again.');
                setLoading(false);
                return;
              }

              if (session?.user?.email_confirmed_at) {
                setVerified(true);
                setLoading(false);
                return;
              }
            }
          }

          if (search) {
            const searchParams = new URLSearchParams(search);
            const type = searchParams.get('type');

            if (type === 'signup' || type === 'email') {
              const { data: { user }, error: userError } = await supabase.auth.getUser();

              if (userError) {
                setError('Unable to verify email status. Please try signing in.');
                setLoading(false);
                return;
              }

              if (user?.email_confirmed_at) {
                setVerified(true);
              } else {
                setError('Email verification failed. Please try again or request a new verification email.');
              }
              setLoading(false);
              return;
            }
          }
        }

        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          setError('Unable to verify email status. Please try signing in.');
          setLoading(false);
          return;
        }

        if (user?.email_confirmed_at) {
          setVerified(true);
        } else if (user) {
          setError('Email verification is still pending. Please check your email and click the verification link.');
        } else {
          setError('No user found. Please sign up first.');
        }
        setLoading(false);
      } catch (err) {
        setError('An error occurred while verifying your email. Please try again.');
        setLoading(false);
      }
    };

    checkVerification();
  }, []);

  const handleContinue = async () => {
    if (!verified) {
      router.replace('/(auth)/sign-in');
      return;
    }
    let userId = user?.id;
    if (!userId && supabase) {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      userId = currentUser?.id ?? undefined;
    }
    if (!userId) {
      router.replace('/(tabs)');
      return;
    }
    setContinuing(true);
    try {
      const completed = await getOnboardingCompleted(userId);
      if (completed) {
        const pendingInviteToken = await AsyncStorage.getItem(PENDING_INVITE_KEY);
        if (pendingInviteToken) {
          await AsyncStorage.removeItem(PENDING_INVITE_KEY);
          router.replace(`/join-invite?token=${encodeURIComponent(pendingInviteToken)}` as any);
          setContinuing(false);
          return;
        }
        router.replace('/(tabs)');
      } else {
        router.replace('/(auth)/onboarding');
      }
    } catch {
      router.replace('/(tabs)');
    } finally {
      setContinuing(false);
    }
  };

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.header}>
            <View style={styles.iconContainer}>
              {loading ? (
                <Ionicons name="mail-open" size={48} color={Brand.primary} />
              ) : verified ? (
                <Ionicons name="checkmark-circle" size={48} color={Brand.primary} />
              ) : (
                <Ionicons name="alert-circle" size={48} color={Brand.destructive} />
              )}
            </View>
            <Text style={styles.title}>Email Verification</Text>
            <Text style={styles.subtitle}>
              {loading
                ? 'Verifying your email...'
                : verified
                  ? 'You are all set!'
                  : 'Verification'}
            </Text>
          </View>

          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Ionicons
                name={verified ? 'checkmark-circle' : loading ? 'mail-open' : 'alert-circle'}
                size={20}
                color={verified ? Brand.primary : loading ? Brand.primary : Brand.destructive}
              />
              <Text style={styles.cardTitle}>
                {loading
                  ? 'Verifying...'
                  : verified
                    ? 'Verification Successful'
                    : 'Verification Failed'}
              </Text>
            </View>

            {loading ? (
              <Text style={styles.message}>
                Please wait while we verify your email address...
              </Text>
            ) : verified ? (
              <>
                <Text style={styles.message}>
                  Your email has been verified. You are now signed in and can start using the app.
                </Text>
                <Button
                  onPress={handleContinue}
                  style={styles.continueButton}
                  disabled={continuing}
                  loading={continuing}
                >
                  Continue to Home
                </Button>
              </>
            ) : (
              <>
                <Text style={styles.errorMessage}>
                  {error || 'Email verification failed. Please try again or request a new verification email.'}
                </Text>
                <Button onPress={handleContinue} style={styles.continueButton} variant="outline">
                  Go to Sign In
                </Button>
              </>
            )}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    justifyContent: 'center',
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 32,
    alignItems: 'center',
  },
  iconContainer: {
    width: 100,
    height: 100,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Brand.primary,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    textAlign: 'center',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 24,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 2,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  message: {
    fontSize: 16,
    color: Neutrals.textPrimary,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  errorMessage: {
    fontSize: 16,
    color: Brand.destructive,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  continueButton: {
    marginTop: 8,
  },
});
